import React, {useEffect, useMemo, useRef, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {UploadReport, SelectSpecialist, PaymentProcess, SecondOpinion, RecentDoctors} from '@/components';
import {useDispatch, useSelector} from 'react-redux';
import {
  patientDashboardCurrentStep,
  patientDashboardFailureState,
  patientDashboardSuccessState,
  newConsultation,
  appSpecializations,
  patientDashboardExpectedCategory,
  patientDashboardPaymentStatus,
  accountDeletionStatusSelector,
  patientDashboardConsultationFlow,
  patientDashboardExpireAt,
} from '@/services/redux/selectors';
import {useToast} from '@/hooks';
import {
  resetPatientDashboardMessage,
  newConsultationCheckRequest,
  getPendingConsultationCaseRequest,
  resetPatientDashboardState,
  getRecentCasesRequest,
  setCurrentConsultationTab,
} from '@/services/redux/slices';
import {resetMessage} from '@/utils/common_functions';
import {getErrorMessage, getSuccessMessage, ErrorCodes, SuccessCodes} from '@/utils/getMessages';
import {ArrowRight} from 'lucide-react';
import {Icon} from '@/ui-kits';
import {Routes} from '@/navigation/Routes';
import {ConsultationGroup, ConsulationFlowType, PatientAccountTicketRequestStatus} from '@/constants/enums';
import {SELECT_SPECIALIST_CHECKPOINT} from '@/constants';

const PatientDashboard: React.FC = () => {
  // ===========================
  // hooks
  // ===========================
  const {showToast} = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // ===========================
  // selectors
  // ===========================
  const currentStep = useSelector(patientDashboardCurrentStep);
  const failure = useSelector(patientDashboardFailureState) as ErrorCodes;
  const success = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const isNewConsultationAvailable = useSelector(newConsultation);
  const expected_category = useSelector(patientDashboardExpectedCategory);
  const specializations = useSelector(appSpecializations);
  const step = useSelector(patientDashboardCurrentStep);
  const paymentStatus = useSelector(patientDashboardPaymentStatus);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const consultationFlow = useSelector(patientDashboardConsultationFlow);
  const expire_at = useSelector(patientDashboardExpireAt);

  // ===========================
  // states
  // ===========================
  const expected_category_name = useMemo(() => {
    return specializations.find(spec => spec.id === expected_category)?.label ?? '';
  }, [expected_category]);

  // Get location state
  const state = location.state as {
    selectedSpecialistId?: number;
    selectedSpecialistName?: string;
    fromSpecialistPage?: boolean;
  } | null;

  const selectedSpecialistName = state?.selectedSpecialistName;
  const paymentRef = useRef<HTMLDivElement | null>(null);
  const secondOpinionRef = useRef<HTMLDivElement | null>(null);

  const handleDoctorAction = useCallback(
    (_doctor: any) => {
      dispatch(getPendingConsultationCaseRequest());
    },
    [dispatch],
  );

  // ===========================
  // side effects
  // ===========================
  useEffect(() => {
    dispatch(newConsultationCheckRequest());
    dispatch(getRecentCasesRequest());
  }, []);

  useEffect(() => {
    // E-10080: Category mismatch (handled by modal)
    // E-10081: Duplicate files (handled by modal)
    // E-10068: Invalid medical file (handled by modal)
    if (failure) {
      if (!['E-10080', 'E-10081'].includes(failure)) {
        showToast(getErrorMessage(failure), 'failure');
      }
      if (failure === 'E-10097') {
        showToast(getErrorMessage(failure, {expected_category: expected_category_name}), 'failure');
      }
    }

    if (success) {
      if (success === 'S-10038') {
        dispatch(setCurrentConsultationTab(ConsultationGroup.Ongoing));
        navigate(Routes.CONSULTATION);
      }
      if (success === 'S-10043') {
        dispatch(getPendingConsultationCaseRequest());
      }
      if (['S-10028'].includes(success) && ![
        ConsulationFlowType.FollowUp, 
        ConsulationFlowType.Referral,
        ConsulationFlowType.HOSPITAL_SPECIALIST_FOLLOWUP,
        ConsulationFlowType.HOSPITAL_SPECIALIST_REFFERAL,
      ].includes(consultationFlow)) {
        showToast(getSuccessMessage(success), 'success');
      }
    }

    // E-10080 managed by modal - skip auto-reset
    if (failure !== 'E-10080' || success) {
      return () =>
        resetMessage(() => {
          dispatch(resetPatientDashboardMessage());
        });
    }
  }, [failure, success, consultationFlow, navigate]);

  // fetch pending consultation case
  useEffect(() => {
    // skip if account deletion status is approved
    if (accountDeletionStatus === PatientAccountTicketRequestStatus.Approved) {
      dispatch(resetPatientDashboardState());
      return;
    }
    // skip if step is 3 or payment is success

    // here step is explicitly ignore from the useEffect dependency because 
    // are manually changing the step in redux i dont want my useEffect to 
    // run when we change the step locally
    if (step !== 3 && paymentStatus !== 'success') {
      dispatch(getPendingConsultationCaseRequest());
    }
  }, [paymentStatus, accountDeletionStatus]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    // when payment becomes success → scroll to payment section
    if (paymentStatus === 'success' && paymentRef.current) {
      timer = setTimeout(() => {
        paymentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 1000);
    }

    // when step becomes 4 → scroll to second opinion section
    if (currentStep === 4 && secondOpinionRef.current) {
      secondOpinionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [paymentStatus, currentStep]);

  // if step is not 2, clear the checkpoint
  useEffect(() => {
    if (currentStep !== 2) {
      localStorage.removeItem(SELECT_SPECIALIST_CHECKPOINT);
    }
  }, [currentStep]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (expire_at) {
      timer = setTimeout(() => {
        dispatch(resetPatientDashboardState());
      }, new Date(expire_at).getTime() - new Date().getTime());
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [expire_at]);

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-4 sm:p-6 lg:p-8 overflow-hidden">
      <div className="px-0 sm:px-4 md:px-8  2xl:px-16  mx-auto flex flex-col">
        {/* Recent Doctors Section (above steps header) */}
        <RecentDoctors onDoctorAction={handleDoctorAction} />

        {/* Header */}
        <div className="mb-8 sm:mb-10">
          <h1 className="text-xl font-FigtreeBold font-bold text-ui_blue text-left">
            Follow these simple steps to get an expert second opinion on your medical case
          </h1>
        </div>

        {/* Notification Banner */}
        {isNewConsultationAvailable && <AwaitingSecondOpinion />}

        {/* Step Components */}
        <div className="w-full max-w-[1536px] flex flex-col gap-6 self-center">
          <UploadReport open={currentStep == 1} success={currentStep > 1} selectedSpecialistName={selectedSpecialistName ?? ''} />
          {/* Hide SelectSpecialist step for hospital assigned flow */}
          {consultationFlow !== ConsulationFlowType.HospitalSpecialistAssigned && <SelectSpecialist open={currentStep == 2} success={currentStep > 2} />}
          {![
            ConsulationFlowType.Referral, 
            ConsulationFlowType.FollowUp,
            ConsulationFlowType.HOSPITAL_SPECIALIST_FOLLOWUP,
            ConsulationFlowType.HOSPITAL_SPECIALIST_REFFERAL,
          ].includes(consultationFlow) && (
            <div ref={paymentRef}>
              <PaymentProcess open={currentStep == 3} success={currentStep > 3} />
            </div>
          )}

          <div ref={secondOpinionRef}>{currentStep == 4 && <SecondOpinion open={currentStep == 4} success={currentStep > 4} />}</div>
        </div>
      </div>
    </div>
  );
};

function AwaitingSecondOpinion() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  function handleGoToConsultation() {
    dispatch(setCurrentConsultationTab(ConsultationGroup.Ongoing));
    navigate(Routes.CONSULTATION, {
      state: {tab: ConsultationGroup.Ongoing},
    });
  }
  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-ui_bg_light_blue p-6 max-w-[1536px] self-center w-full">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10  rounded-lg flex items-center justify-center">
            <Icon type="await" className="size-8" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 rounded-2xl">
          <h3 className="text-2xl font-FigtreeBold text-ui_blue mb-1">Awaiting second opinion report (up to 48 hrs)</h3>
          <p className="text-base font-FigtreeRegular text-ui_input_border_dark mb-3">
            You have consultations awaiting review. To view detailed information about each consultation, please go to the Consultations page.
          </p>

          {/* Link */}
          <button onClick={handleGoToConsultation} className="inline-flex items-center gap-2 text-lg font-FigtreeBold text-ui_blue  transition-colors">
            Go to Consultation
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PatientDashboard;
